import { graphql, useStaticQuery } from "gatsby";
import { useMemo } from "react";

const DEFAULT_LANG: Language =
  (process.env.GATSBY_DEFAULT_LANG as Language) || "en";

/**
 * Resolves the Organization config story for JSON-LD.
 *
 * This space is folder-based (`en/`, and any later locale folders) rather than
 * Storyblok field i18n, so each locale has its own `{lang}/config/organization`
 * story. Matching uses `full_slug`, falling back to English then any entry.
 */
export default function useOrganizationConfig(language: Language) {
  const data = useStaticQuery<{
    organizations: {
      edges: {
        node: { full_slug: string; content: string };
      }[];
    };
  }>(graphql`
    query OrganizationConfigQuery {
      organizations: allStoryblokEntry(
        filter: { field_component: { eq: "config_organization" } }
      ) {
        edges {
          node {
            full_slug
            content
          }
        }
      }
    }
  `);

  return useMemo(() => {
    const entries = (data?.organizations?.edges || []).map(({ node }) => ({
      full_slug: node.full_slug,
      content: JSON.parse(node.content) as OrganizationConfigType,
    }));

    const match =
      entries.find(({ full_slug }) => full_slug.startsWith(`${language}/`)) ||
      entries.find(({ full_slug }) =>
        full_slug.endsWith(`${DEFAULT_LANG}/config/organization`),
      ) ||
      entries.find(
        ({ full_slug }) =>
          full_slug === "config/organization" ||
          full_slug.endsWith("/config/organization"),
      ) ||
      entries[0];

    return match?.content;
  }, [data, language]);
}
